import { Input, Popover, PopoverSurface, PopoverTrigger, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import type { CalendarStrings, DateFormatting } from "@fluentui/react-calendar-compat";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import { Dismiss16Regular } from "@fluentui/react-icons";
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { FuiButton } from "@/components/fui-control";
import { useDialogLayer } from "@/components/ui/dialog-layer";
import { usePortalContainer } from "@/components/ui/use-portal-container";
import { Calendar } from "@/components/ui/calendar";
import { todayISO, type ISODate } from "@/engine/date";
import { dateFnsLocale, formatDate, monthNames, weekdayNames } from "@/lib/date-format";
import { datePresets, presetInRange } from "@/lib/date-presets";
import { cn } from "@/lib/utils";
import { useProjectStore } from "@/store/project-store";

// Month and weekday names come from the shared date locale; only the UI labels are written here.
const koStrings: CalendarStrings = {
  months: monthNames("long"),
  shortMonths: monthNames("short"),
  days: weekdayNames("long"),
  shortDays: weekdayNames("short"),
  goToToday: "오늘",
  prevMonthAriaLabel: "이전 달",
  nextMonthAriaLabel: "다음 달",
  prevYearAriaLabel: "이전 해",
  nextYearAriaLabel: "다음 해",
  prevYearRangeAriaLabel: "이전 연도 범위",
  nextYearRangeAriaLabel: "다음 연도 범위",
  monthPickerHeaderAriaLabel: "{0}, 연도 선택",
  yearPickerHeaderAriaLabel: "{0}, 월 선택",
  closeButtonAriaLabel: "닫기",
  weekNumberFormatString: "{0}주",
  selectedDateFormatString: "선택한 날짜 {0}",
  todayDateFormatString: "오늘 {0}",
  dayMarkedAriaLabel: "표시된 날짜",
};

const koDateTimeFormatter: DateFormatting = {
  formatDay: (date) => formatDate(date, "day"),
  formatMonth: (date) => formatDate(date, "monthName"),
  formatYear: (date) => formatDate(date, "year"),
  formatMonthDayYear: (date) => formatDate(date, "full"),
  formatMonthYear: (date) => formatDate(date, "yearMonth"),
};

function isoToLocalDate(iso: ISODate): Date {
  const [y, m, d] = iso.split("-").map(Number) as [number, number, number];
  return new Date(y, m - 1, d);
}
function localDateToISO(d: Date): ISODate {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function clampISO(iso: ISODate, min?: ISODate, max?: ISODate): ISODate {
  if (min && iso < min) return min;
  if (max && iso > max) return max;
  return iso;
}

function formatPickedDate(date?: Date): string {
  if (!date || Number.isNaN(date.getTime())) return "";
  return formatDate(date, "full");
}

const useStyles = makeStyles({
  input: {
    width: "100%",
    minWidth: 0,
    flexGrow: 1,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
  },
  page: {
    minHeight: "28px",
    height: "28px",
    maxHeight: "28px",
  },
  popup: {
    /* Dialog content is z-50 inside the same layer. The popup must sit above it.
       A modal dialog sets pointer-events:none on body; the popup is a sibling of
       the dialog content, so it must opt back in or clicks fall through. */
    zIndex: 1000000,
    pointerEvents: "auto",
    "& td[data-nonworking='true']:not([aria-selected='true']) button": {
      color: tokens.colorNeutralForeground3,
      backgroundColor: "var(--nonworking)",
    },
  },
  presets: {
    display: "flex",
    flexWrap: "wrap",
    gap: "4px",
    paddingBottom: tokens.spacingVerticalXS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    marginBottom: tokens.spacingVerticalXS,
  },
  presetOn: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorNeutralForeground1,
  },
  presetClear: {
    color: tokens.colorPaletteRedForeground1,
  },
});

/**
 * Popup mount: the open dialog's layer when inside one (so it stacks above that dialog),
 * otherwise the drawer's portal (so a modal drawer does not mark it inert),
 * otherwise the shared #dialog-layer.
 */
function useDatePickerMountNode() {
  const dialogLayer = useDialogLayer();
  const fallback = usePortalContainer();
  const anchorRef = useRef<HTMLDivElement>(null);
  const [mountNode, setMountNode] = useState<HTMLElement | undefined>(dialogLayer ?? fallback);
  // A drawer is its own Fluent portal with a focus trap. A second portal for the
  // calendar lands outside that trap, so day clicks never reach React. Render inline.
  const [inlinePopup, setInlinePopup] = useState(false);

  useLayoutEffect(() => {
    if (dialogLayer) {
      setInlinePopup(false);
      setMountNode(dialogLayer);
      return;
    }
    const drawer = anchorRef.current?.closest(".fui-OverlayDrawer");
    if (drawer) {
      setInlinePopup(true);
      return;
    }
    setInlinePopup(false);
    setMountNode(fallback);
  }, [dialogLayer, fallback]);

  return { anchorRef, mountNode, inlinePopup };
}

/** Project calendar-aware day picker (non-working days are dimmed, today is marked). Used by the WBS cell editor. */
export function DateCalendar({
  value,
  onSelect,
  defaultMonth,
  min,
  max,
}: {
  value?: ISODate;
  onSelect: (iso: ISODate) => void;
  defaultMonth?: ISODate;
  /** inclusive lower bound — days before this are disabled */
  min?: ISODate;
  /** inclusive upper bound — days after this are disabled */
  max?: ISODate;
}) {
  const calendar = useProjectStore((s) => s.derived.calendar);
  const selected = value ? isoToLocalDate(value) : undefined;
  const disabled = [
    ...(min ? [{ before: isoToLocalDate(min) }] : []),
    ...(max ? [{ after: isoToLocalDate(max) }] : []),
  ];
  return (
    <Calendar
      mode="single"
      locale={dateFnsLocale()}
      selected={selected}
      defaultMonth={selected ?? (defaultMonth ? isoToLocalDate(defaultMonth) : undefined)}
      captionLayout="dropdown"
      startMonth={new Date(2015, 0)}
      endMonth={new Date(2040, 11)}
      disabled={disabled.length ? disabled : undefined}
      onSelect={(d) => d && onSelect(clampISO(localDateToISO(d), min, max))}
      modifiers={{ nonworking: (d) => !calendar.isWorkingISO(localDateToISO(d)) }}
      modifiersClassNames={{ nonworking: "[&>button]:text-muted-foreground/60 bg-nonworking rounded-none" }}
      className="min-w-[252px] p-2 [--cell-size:--spacing(8)] text-body"
    />
  );
}

/** 오늘 · 내일 · 다음 주 · 2주 뒤. 값이 있으면 지우기. */
export function DatePresets({
  value,
  onSelect,
  min,
  max,
  today = todayISO(),
}: {
  value?: ISODate;
  onSelect: (iso: ISODate | undefined) => void;
  min?: ISODate;
  max?: ISODate;
  today?: ISODate;
}) {
  const styles = useStyles();
  return (
    <div className={styles.presets}>
      {datePresets(today).map((preset) => {
        const disabled = !presetInRange(preset.iso, min, max);
        const on = value === preset.iso;
        return (
          <FuiButton
            key={preset.id}
            appearance="subtle"
            size="small"
            className={on ? styles.presetOn : undefined}
            disabled={disabled}
            aria-pressed={on}
            onClick={() => onSelect(preset.iso)}
          >
            {preset.label}
          </FuiButton>
        );
      })}
      {value && (
        <FuiButton appearance="subtle" size="small" className={styles.presetClear} icon={<Dismiss16Regular />} onClick={() => onSelect(undefined)}>
          지우기
        </FuiButton>
      )}
    </div>
  );
}

export interface DateFieldProps {
  value?: ISODate;
  onChange: (iso: ISODate | undefined) => void;
  /** month to open on when there is no value */
  defaultMonth?: ISODate;
  /** inclusive lower bound */
  min?: ISODate;
  /** inclusive upper bound */
  max?: ISODate;
  placeholder?: string;
  clearable?: boolean;
  disabled?: boolean;
  className?: string;
  /** trigger height: page 28px, dialog 24px */
  size?: "sm" | "md";
  /** 달력 위에 오늘·내일·다음 주·2주 뒤 */
  presets?: boolean;
  "aria-label"?: string;
}

/** Date field backed by Fluent DatePicker. Values stay local `YYYY-MM-DD` strings. */
function PickerDateField({
  value,
  onChange,
  defaultMonth,
  min,
  max,
  placeholder = "날짜 선택",
  clearable,
  disabled,
  className,
  size = "md",
  ...rest
}: DateFieldProps) {
  const styles = useStyles();
  const calendar = useProjectStore((s) => s.derived.calendar);
  const { anchorRef, mountNode, inlinePopup } = useDatePickerMountNode();
  const selected = useMemo(() => (value ? isoToLocalDate(value) : null), [value]);
  const initialPickerDate = useMemo(() => (defaultMonth ? isoToLocalDate(defaultMonth) : undefined), [defaultMonth]);
  const minDate = useMemo(() => isoToLocalDate(min ?? "2015-01-01"), [min]);
  const maxDate = useMemo(() => isoToLocalDate(max ?? "2040-12-31"), [max]);
  const markDay = useCallback(
    (element: HTMLElement | null, date: Date) => {
      if (!element) return;
      element.dataset.nonworking = calendar.isWorkingISO(localDateToISO(date)) ? "false" : "true";
    },
    [calendar],
  );

  return (
    <div ref={anchorRef} className={cn("flex min-w-0 items-center gap-1", className)}>
      <DatePicker
        size="small"
        className={mergeClasses(styles.input, size === "md" && styles.page)}
        appearance="outline"
        placeholder={placeholder}
        disabled={disabled}
        aria-label={rest["aria-label"]}
        value={selected}
        formatDate={formatPickedDate}
        strings={koStrings}
        dateTimeFormatter={koDateTimeFormatter}
        initialPickerDate={initialPickerDate}
        minDate={minDate}
        maxDate={maxDate}
        showMonthPickerAsOverlay
        mountNode={inlinePopup ? undefined : mountNode}
        inlinePopup={inlinePopup}
        positioning={{ position: "below", align: "start" }}
        popupSurface={{ className: styles.popup, style: inlinePopup ? { zIndex: 2 } : undefined }}
        calendar={{ calendarDayProps: { customDayCellRef: markDay } }}
        onSelectDate={(date) => {
          if (!date) {
            onChange(undefined);
            return;
          }
          onChange(clampISO(localDateToISO(date), min, max));
        }}
      />
      {clearable && value && (
        <FuiButton
          appearance="subtle"
          density={size === "sm" ? "dialog" : "page"}
          icon={<Dismiss16Regular />}
          aria-label="해제"
          disabled={disabled}
          onClick={() => onChange(undefined)}
        />
      )}
    </div>
  );
}

function PresetDateField({
  value,
  onChange,
  min,
  max,
  placeholder = "날짜 선택",
  disabled,
  className,
  size = "md",
  ...rest
}: DateFieldProps) {
  const styles = useStyles();
  const { anchorRef, mountNode, inlinePopup } = useDatePickerMountNode();
  const [open, setOpen] = useState(false);
  const pick = (iso: ISODate | undefined) => {
    onChange(iso ? clampISO(iso, min, max) : undefined);
    setOpen(false);
  };
  return (
    <div ref={anchorRef} className={cn("flex min-w-0 items-center gap-1", className)}>
      <Popover
        open={open}
        inline={inlinePopup}
        mountNode={inlinePopup ? undefined : mountNode}
        positioning={{ position: "below", align: "start" }}
        onOpenChange={(_, data) => setOpen(data.open)}
      >
        <PopoverTrigger disableButtonEnhancement>
          <Input
            className={mergeClasses(styles.input, size === "md" && styles.page)}
            size="small"
            appearance="outline"
            readOnly
            disabled={disabled}
            placeholder={placeholder}
            value={value ? formatDate(value, "full") : ""}
            aria-label={rest["aria-label"]}
            aria-expanded={open}
          />
        </PopoverTrigger>
        <PopoverSurface className={mergeClasses("date-presets", styles.popup)} style={inlinePopup ? { zIndex: 2, padding: "8px" } : { padding: "8px" }}>
          <DatePresets value={value} min={min} max={max} onSelect={pick} />
          <DateCalendar value={value} min={min} max={max} onSelect={(iso) => pick(iso)} />
        </PopoverSurface>
      </Popover>
    </div>
  );
}

/** 날짜 필드. `presets`면 달력 위에 오늘·내일·다음 주·2주 뒤를 둔다. */
export function DateField(props: DateFieldProps) {
  if (props.presets) return <PresetDateField {...props} />;
  return <PickerDateField {...props} />;
}
